import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import { getDefaultWarehouseId, selectWarehouse, useCurrentWarehouseId } from '../../lib/warehouse';
import type { WarehouseListItem } from './types';

/**
 * F1 首次进入流程：仅 1 个可访问仓库时自动跳过选择页；记住的默认仓库自动
 * 直达；≥2 个且无默认时跳转到 /warehouses（S2）手动选择；0 个时同样跳转到
 * /warehouses，由该页自身渲染"未获授权访问任何仓库"的空状态。
 *
 * 只在还没有当前仓库时才查询——已经选好之后，切换仓库走 <WarehouseSwitcher>，
 * 不需要这个守卫重新介入。
 */
export function RequireWarehouse(): React.JSX.Element {
  const currentId = useCurrentWarehouseId();
  const queryClient = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ['warehouses', 'mine'],
    queryFn: () => apiFetch<{ items: WarehouseListItem[] }>('/warehouses/mine'),
    enabled: !currentId,
  });

  useEffect(() => {
    if (currentId || !data) return;
    const defaultId = getDefaultWarehouseId();
    if (defaultId && data.items.some((w) => w.id === defaultId)) {
      selectWarehouse(defaultId, true);
    } else if (data.items.length === 1) {
      selectWarehouse(data.items[0]!.id, false);
    } else {
      return;
    }
    void queryClient.invalidateQueries({ queryKey: ['session', 'me'] });
  }, [currentId, data, queryClient]);

  if (currentId) return <Outlet />;
  if (isPending || !data) return <></>;
  if (data.items.length === 0) return <Navigate to="/warehouses" replace />;

  const defaultId = getDefaultWarehouseId();
  const autoSelectable =
    data.items.length === 1 ||
    (defaultId !== undefined && data.items.some((w) => w.id === defaultId));
  // 上面的 effect 会在下一个 tick 完成自动选择并触发重渲染；这里短暂留白
  // 而不是先闪一下选择页再跳走。
  if (autoSelectable) return <></>;

  return <Navigate to="/warehouses" replace />;
}
